// hooks/useVendors.js
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getVendors,
    createVendor,
    deleteVendor,
    editVendor,
    createCosts,
    getVendorById,
    deleteAreaCost,
    editSchudle,
    editVendorSchudle,
    copyCosts,
    getVendorsDropdown,
} from "services/vendors";
import { setVendors } from "store/vendorSlice";
const useVendors = () => {
    const vendors = useSelector((state) => state.vendor.vendors);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const dispatch = useDispatch();

    // Fetch Vendors
    const fetchVendors = async () => {
        setLoading(true);
        try {
            const data = await getVendors();

            dispatch(setVendors(data.results));
        } catch (err) {
            setError(err.message || "Failed to fetch Vendors");
        } finally {
            setLoading(false);
        }
    };

    // Create Vendor
    const addVendor = async (newVendor) => {
        setLoading(true);
        try {
            const response = await createVendor(newVendor);
            return response;
        } catch (err) {
            setError(err.response.data.message || "Failed to create Vendor");
            return err;
        } finally {
            setLoading(false);
        }
    };

    // Create Vendor
    const addDeliveryCost = async (costs) => {
        setLoading(true);
        try {
            const response = await createCosts(costs);
            return response;
        } catch (err) {
            setError(err.response.data.message || "Failed to create Vendor");
            return err;
        } finally {
            setLoading(false);
        }
    };

    const copyDeliveryCost = async (data) => {
        setLoading(true);
        try {
            const response = await copyCosts(data);
            fetchVendors();
            return response;
        } catch (err) {
            setError(err.response.data.message || "Failed to create Vendor");
            return err;
        } finally {
            setLoading(false);
        }
    };

    const deleteArea = async (id) => {
        setLoading(true);
        try {
            await deleteAreaCost(id);
            return true;
        } catch (err) {
            setError(err.message || "Failed to delete Product");
            return err;
        } finally {
            setLoading(false);
        }
    };
    // Create Vendor
    const getVendor = async (id) => {
        setLoading(true);
        try {
            const response = await getVendorById(id);
            return response;
        } catch (err) {
            setError(err.response.data.message || "Failed to create Vendor");
            return err;
        } finally {
            setLoading(false);
        }
    };

    const getVendorSchudle = async (id) => {
        setLoading(true);
        try {
            const response = await getVendorById(id);
            return response;
        } catch (err) {
            setError(err.response.data.message || "Failed to create Vendor");
            return err;
        } finally {
            setLoading(false);
        }
    };
    // Edit Vendor
    const updateVendor = async (updatedData) => {
        setLoading(true);
        try {
            const updated = await editVendor(updatedData);
            fetchVendors();
            return updated;
        } catch (err) {
            setError(err.message || "Failed to update Vendor");
        } finally {
            setLoading(false);
        }
    };

    const updateVendorsSchudle = async (data) => {
        setLoading(true);
        try {
            const updated = await editSchudle(data);
            return updated;
        } catch (err) {
            const msg = err.response?.data?.message || err.message || "Failed to update schedule";
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    };
    const updateVendorSchudle = async (id, data) => {
        setLoading(true);
        try {
            const updated = await editVendorSchudle(id, data);
            return updated;
        } catch (err) {
            const msg = err.response?.data?.message || err.message || "Failed to update schedule";
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    };
    // Delete Vendor
    const removeVendor = async (id) => {
        setLoading(true);
        try {
            await deleteVendor(id);

            dispatch(setVendors(vendors.filter((vendor) => vendor.id !== id)));

            return true;
        } catch (err) {
            setError(err.message || "Failed to delete Vendor");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVendors(); // Fetch initial data
    }, []);

    return {
        vendors,
        loading,
        error,
        fetchVendors,
        addVendor,
        updateVendor,
        removeVendor,
        addDeliveryCost,
        getVendor,
        deleteArea,
        updateVendorsSchudle,
        getVendorSchudle,
        updateVendorSchudle,
        copyDeliveryCost,
    };
};

export default useVendors;

// --- TanStack Query hooks (used on /vendors page) ---

export const vendorKeys = {
    all: ["vendors"],
};

/**
 * Fetches vendors via TanStack Query with server-side pagination and filtering.
 * Reads page/size/nameFilter/cityFilter from the vendorSlice so that dispatching
 * a filter or page action automatically triggers a refetch.
 * Results are synced back into Redux so existing slice consumers keep working.
 */
export const useGetVendors = () => {
    const dispatch = useDispatch();
    const currentPage = useSelector((state) => state.vendor.currentPage);
    const size = useSelector((state) => state.vendor.size);
    const nameFilter = useSelector((state) => state.vendor.nameFilter);
    const cityFilter = useSelector((state) => state.vendor.cityFilter);
    const appStatusFilter = useSelector((state) => state.vendor.appStatusFilter);
    const dashboardStatusFilter = useSelector((state) => state.vendor.dashboardStatusFilter);
    const orderView = useSelector((state) => state.vendor.orderView);
    const sortBy = useSelector((state) => state.vendor.sortBy);
    const sortDir = useSelector((state) => state.vendor.sortDir);

    const params = { page: currentPage, size };
    if (orderView === "pinned") params.pinned = "true";
    if (orderView === "customer") params.customerOrder = "true";
    if (nameFilter) params.name = nameFilter;
    if (cityFilter) params.cityId = cityFilter;
    if (appStatusFilter) params.appStatus = appStatusFilter;
    if (dashboardStatusFilter) params.dashboardStatus = dashboardStatusFilter;
    if (sortBy) {
        params.sortBy = sortBy;
        params.sortDir = sortDir || "asc";
    }

    const query = useQuery({
        queryKey: [
            ...vendorKeys.all,
            {
                page: currentPage,
                size,
                name: nameFilter,
                cityId: cityFilter,
                appStatus: appStatusFilter,
                dashboardStatus: dashboardStatusFilter,
                orderView,
                sortBy,
                sortDir,
            },
        ],
        queryFn: () => getVendors(params),
        // Keep showing previous page's data while the next page/filter loads so the
        // UI never unmounts (which would reset uncontrolled inputs like the search box).
        placeholderData: (prev) => prev,
        // The التطبيق column ages in real time — without this it would sit on a
        // stale timestamp until the operator navigated. 30s keeps it honest
        // against the 60s "مفتوح" threshold without hammering the list query.
        refetchInterval: 30_000,
    });

    // Sync query data into Redux so Vendor.js can read current-page data from the store
    useEffect(() => {
        if (query.data) {
            dispatch(setVendors(query.data));
        }
    }, [query.data, dispatch]);

    return {
        vendors: query.data?.results ?? [],
        loading: query.isLoading, // true only on the very first fetch (no data yet)
        isFetching: query.isFetching, // true on every background refetch
        error: query.error?.message ?? null,
        refetch: query.refetch,
    };
};

export const useUpdateVendor = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: editVendor,
        onMutate: async (formData) => {
            // Cancel any in-flight refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: vendorKeys.all });

            // Snapshot current cache for rollback
            const previousVendors = queryClient.getQueryData(vendorKeys.all);

            // Optimistically patch the changed fields in the cache
            const id = Number(formData.get("id"));
            const status = formData.get("status");

            if (previousVendors && status) {
                queryClient.setQueryData(
                    vendorKeys.all,
                    previousVendors.map((v) => (v.id === id ? { ...v, status } : v)),
                );
            }

            return { previousVendors };
        },
        onError: (_err, _formData, context) => {
            // Rollback to the previous cache on failure
            if (context?.previousVendors) {
                queryClient.setQueryData(vendorKeys.all, context.previousVendors);
            }
        },
        onSettled: () => {
            // Background refetch to ensure server consistency
            queryClient.invalidateQueries({ queryKey: vendorKeys.all });
        },
    });
};

export const useRemoveVendor = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteVendor,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: vendorKeys.all });
        },
    });
};

export const useCopyDeliveryCost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: copyCosts,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: vendorKeys.all });
        },
    });
};

export const useUpdateVendorsSchedule = () => {
    return useMutation({
        mutationFn: editSchudle,
    });
};

export const useUpdateVendorSchedule = () => {
    return useMutation({
        mutationFn: ({ id, data }) => editVendorSchudle(id, data),
    });
};

// --- Lightweight dropdown hook ---
// Used by any component that only needs id/name/cityId/vendorId for a <select>.
// Fetches from /vendor/get-vendors-dropdown — no deep includes, no pagination.

export const vendorDropdownKeys = {
    all: ["vendors-dropdown"],
    list: (params) => [...vendorDropdownKeys.all, { params }],
};

/**
 * Returns a minimal vendor list suitable for populating dropdowns.
 * Each item: { id, name, cityId, vendorId }
 * TanStack caches this for the lifetime of the page — only one network request
 * regardless of how many components call this hook simultaneously.
 */
export const useGetVendorsDropdown = (params = {}) => {
    const query = useQuery({
        queryKey: vendorDropdownKeys.list(params),
        queryFn: async () => {
            const data = await getVendorsDropdown(params);
            return data.results;
        },
    });

    return {
        vendors: query.data ?? [],
        loading: query.isLoading,
        error: query.error?.message ?? null,
    };
};
